<script setup lang="ts">
	import { computed, nextTick, onMounted, ref } from 'vue'
	import { useI18n } from 'vue-i18n'
	import { toast } from '@/libs/toaster'
	import { toErrorMessage } from '@/libs/errorToaster'
	import { ChevronDown } from 'lucide-vue-next'
	import VModalFinal from '@/components/VModalFinal.vue'
	import Floater from '@/components/common/Floater.vue'
	import ConfirmButton from '@/components/ConfirmButton.vue'
	import StatusLight from '@/components/StatusLight.vue'
	import { useVfm } from '@/libs/modals'
	import { _translations } from './_translations'
	import { useLocationStore } from '@/stores/location.store'
	import { useHubStore } from '@/stores/hubs.store'
	import { useComappStore } from '@/stores/comapps.store'

	const props = defineProps<{
		modalId: string
		locationId: string | null
	}>()

	const emit = defineEmits<{ refresh: [] }>()

	const { t: _tr } = useI18n(_translations)
	const vfm = useVfm()
	const locationStore = useLocationStore()
	const hubStore = useHubStore()
	const comappStore = useComappStore()

	const isAssigningHub = ref(false)
	const isAssigningComapp = ref(false)
	const hubSearch = ref('')
	const comappSearch = ref('')
	const hubSearchInput = ref<HTMLInputElement | null>(null)
	const comappSearchInput = ref<HTMLInputElement | null>(null)

	// Manual focus on open — `autofocus` attribute is unreliable for inputs
	// teleported into Vue's <Transition>, so we focus via ref after the panel
	// is in the DOM. Two nextTicks for safety: the first lets Floater toggle
	// `v-show`, the second lets the input ref attach.
	const focusHubSearch = async () => {
		await nextTick()
		await nextTick()
		hubSearchInput.value?.focus()
	}
	const focusComappSearch = async () => {
		await nextTick()
		await nextTick()
		comappSearchInput.value?.focus()
	}

	// Case-insensitive `includes` filter — matches name, status, or anything
	// else the user might recognise the device by.
	const matchesQuery = (text: string, query: string) => {
		const q = query.trim().toLowerCase()
		if (!q) return true
		return text.toLowerCase().includes(q)
	}

	const locationName = computed(() => {
		const locId = props.locationId || ''
		if (!locId) return ''
		const loc = locationStore.locationsMap[locId]
		return loc?.name || loc?.meta?.name || locId
	})

	const modalTitle = computed(() => {
		const base = _tr('location.assignDevices')
		return locationName.value ? `${base} · ${locationName.value}` : base
	})

	const isOrphan = (locId?: string): boolean => {
		if (!locId) return false
		return !locationStore.locationsMap[locId]
	}

	const getLocationLabel = (locId?: string) => {
		if (!locId) return _tr('location.unassigned')
		const loc = locationStore.locationsMap[locId]
		if (loc) return loc.name || loc.meta?.name || locId
		return _tr('location.deletedLocation')
	}

	const getAssignmentLabel = (locId?: string) => {
		if (!locId) return `· ${_tr('location.unassigned')}`
		if (isOrphan(locId)) return `⚠ ${_tr('location.deletedLocation')}`
		return _tr('location.assignedTo', { name: getLocationLabel(locId) })
	}

	// Assignable list = every hub/comapp that is NOT already at this location.
	// `orphan` is surfaced as a flag (instead of a separate panel) so the
	// Floater dropdown can render both kinds inline — with the right actions
	// per row. Orphans are sorted to the top so they catch the eye.
	// Three distinct row states with different visual cues:
	//   orphan      — locationId points at a deleted location  → yellow (warn)
	//   unassigned  — no locationId at all                       → blue   (info)
	//   assigned    — assigned to a different live location      → neutral
	// The dropdown sorts orphans first (need cleanup), then unassigned (ready
	// to grab), then live-assigned (require explicit move).
	const assignableHubs = computed(() => {
		if (!props.locationId) return []
		return hubStore.hubs
			.filter((hub) => hub.locationId !== props.locationId)
			.map((hub) => ({
				value: hub._id,
				label: hub.name || hub.serial || hub._id,
				locationLabel: getAssignmentLabel(hub.locationId),
				orphan: !!hub.locationId && isOrphan(hub.locationId),
				unassigned: !hub.locationId,
			}))
			.sort((a, b) => {
				const rank = (x: { orphan: boolean; unassigned: boolean }) =>
					x.orphan ? 0 : x.unassigned ? 1 : 2
				const ra = rank(a), rb = rank(b)
				if (ra !== rb) return ra - rb
				return a.label.localeCompare(b.label)
			})
	})

	const assignableComapps = computed(() => {
		if (!props.locationId) return []
		return comappStore.comapps
			.filter((comapp) => comapp.locationId !== props.locationId)
			.map((comapp) => ({
				value: comapp._id,
				label: comapp.pcName || comapp._id,
				locationLabel: getAssignmentLabel(comapp.locationId),
				orphan: !!comapp.locationId && isOrphan(comapp.locationId),
				unassigned: !comapp.locationId,
			}))
			.sort((a, b) => {
				const rank = (x: { orphan: boolean; unassigned: boolean }) =>
					x.orphan ? 0 : x.unassigned ? 1 : 2
				const ra = rank(a), rb = rank(b)
				if (ra !== rb) return ra - rb
				return a.label.localeCompare(b.label)
			})
	})

	const filteredAssignableHubs = computed(() =>
		assignableHubs.value.filter((h) => matchesQuery(`${h.label} ${h.locationLabel}`, hubSearch.value)),
	)

	const filteredAssignableComapps = computed(() =>
		assignableComapps.value.filter((c) => matchesQuery(`${c.label} ${c.locationLabel}`, comappSearch.value)),
	)

	const orphanCount = computed(() =>
		assignableHubs.value.filter((h) => h.orphan).length +
		assignableComapps.value.filter((c) => c.orphan).length,
	)

	const assignedHubs = computed(() => {
		if (!props.locationId) return []
		const list = hubStore.hubsByLoc?.[props.locationId] || []
		return list
			.slice()
			.sort((a, b) => (a.name || a.serial || a._id).localeCompare(b.name || b.serial || b._id))
	})

	const assignedComapps = computed(() => {
		if (!props.locationId) return []
		const list = comappStore.comappsByLId?.[props.locationId] || []
		return list
			.slice()
			.sort((a, b) => (a.pcName || a._id).localeCompare(b.pcName || b._id))
	})

	const reassignHubToHere = async (hubId: string) => {
		if (!props.locationId || !hubId) return
		isAssigningHub.value = true
		try {
			await hubStore.updateHub({ _id: hubId, locationId: props.locationId } as any)
			await hubStore.listAllHubs()
			toast.success(_tr('location.hubAssigned'))
		} catch (error: any) {
			toast.error(toErrorMessage(error, _tr('location.assignFailed')))
		} finally {
			isAssigningHub.value = false
		}
	}

	const reassignComappToHere = async (comappId: string) => {
		if (!props.locationId || !comappId) return
		isAssigningComapp.value = true
		try {
			await comappStore.updateComapp({ _id: comappId, locationId: props.locationId } as any)
			toast.success(_tr('location.comappAssigned'))
		} catch (error: any) {
			toast.error(toErrorMessage(error, _tr('location.assignFailed')))
		} finally {
			isAssigningComapp.value = false
		}
	}

	const deassignHub = async (hubId: string) => {
		if (!hubId) return
		isAssigningHub.value = true
		try {
			await hubStore.updateHub({ _id: hubId, locationId: '' } as any)
			await hubStore.listAllHubs()
			toast.success(_tr('location.hubUnassigned'))
		} catch (error: any) {
			toast.error(toErrorMessage(error, _tr('location.assignFailed')))
		} finally {
			isAssigningHub.value = false
		}
	}

	const deassignComapp = async (comappId: string) => {
		if (!comappId) return
		isAssigningComapp.value = true
		try {
			await comappStore.updateComapp({ _id: comappId, locationId: '' } as any)
			toast.success(_tr('location.comappUnassigned'))
		} catch (error: any) {
			toast.error(toErrorMessage(error, _tr('location.assignFailed')))
		} finally {
			isAssigningComapp.value = false
		}
	}

	// Triggered when the modal is fully closed (X button, overlay click, or
	// bottom "Schließen"). Re-fetches hubs + comapps so any inline action
	// (assign/deassign) is reflected on the parent LocationView sidebar.
	// Errors are swallowed — the toasts from individual actions already told
	// the user what's wrong.
	const handleClose = async () => {
		try { await hubStore.listAllHubs() } catch {}
		try { await comappStore.listComapps() } catch {}
		emit('refresh')
	}

	const closeModal = () => {
		vfm.close(props.modalId)
	}

	onMounted(async () => {
		try {
			if (!locationStore.locations.length) await locationStore.listLocations()
		} catch (error) {
			console.error('Failed to load locations', error)
		}
		try {
			if (!hubStore.hubs.length) await hubStore.listAllHubs()
		} catch (error) {
			console.error('Failed to load hubs', error)
		}
		try {
			if (!comappStore.comapps.length) await comappStore.listComapps()
		} catch (error) {
			console.error('Failed to load comapps', error)
		}
	})
</script>

<template>
	<VModalFinal
		:modalId="modalId"
		:title="modalTitle"
		:closeOnBackgroudClick="false"
		:showButtons="false"
		:closeButton="true"
		size="xl"
		@close="handleClose">
		<div class="max-h-[75vh] min-h-[60vh] space-y-4 overflow-auto p-4">
			<div class="text-sm text-base-content/60 flex items-center gap-2">
				<span>{{ _tr('location.assignHint') }}</span>
				<span
					v-if="orphanCount > 0"
					class="text-warning text-xs font-medium">
					⚠ {{ orphanCount }} {{ _tr('location.orphanedTitle') }}
				</span>
			</div>

			<div class="grid gap-4 md:grid-cols-2">
				<!-- HUB ASSIGN — Floater dropdown that lists every assignable hub
					 (incl. orphans, sorted to top). Each row has its own action
					 buttons inline, so no separate "Zuweisen" step. -->
				<div class="border-base-300 bg-base-200/50 rounded-lg border p-4">
					<div class="flex items-center justify-between gap-2">
						<h3 class="text-base font-semibold">{{ _tr('location.assignHub') }}</h3>
						<span class="text-xs text-base-content/60">{{ assignableHubs.length }}</span>
					</div>
					<div class="mt-3">
						<Floater
							placement="bottom-start"
							trigger-tag="div"
							trigger-class="block w-full"
							panel-class="w-[40rem] max-w-[92vw]"
							:close-on-outside="true"
							:auto-focus="false"
							@open="focusHubSearch"
							@close="hubSearch = ''">
							<button
								type="button"
								class="btn btn-block btn-outline justify-between gap-2"
								:disabled="!assignableHubs.length">
								<span>{{ _tr('location.selectHub') }}</span>
								<ChevronDown :size="16" />
							</button>
							<template #content="{ close }">
								<div class="flex flex-col">
									<div class="sticky top-0 z-10 bg-base-100 border-b border-base-300/50 p-1">
										<input
											ref="hubSearchInput"
											v-model="hubSearch"
											type="search"
											class="input input-bordered w-full"
											:placeholder="_tr('location.searchPlaceholder')" />
									</div>
									<div class="max-h-96 overflow-auto">
										<p
											v-if="!filteredAssignableHubs.length"
											class="px-3 py-2 text-sm text-base-content/60">
											{{ assignableHubs.length ? _tr('location.noSearchMatch') : _tr('location.noAssignableHubs') }}
										</p>
										<div
											v-for="hub in filteredAssignableHubs"
											:key="hub.value"
											class="flex items-center gap-3 rounded px-2 py-1.5 hover:bg-base-200/60"
											:class="hub.orphan ? 'border-l-2 border-warning/60' : hub.unassigned ? 'border-l-2 border-info/60' : ''">
											<div class="min-w-0 flex-1">
												<div class="truncate font-medium">{{ hub.label }}</div>
												<div
													class="text-xs"
													:class="hub.orphan ? 'text-warning' : hub.unassigned ? 'text-info' : 'text-base-content/60'">
													{{ hub.locationLabel }}
												</div>
											</div>
											<div class="flex items-center gap-1 shrink-0">
												<ConfirmButton
													modal
													:title="_tr('location.assignToHere')"
													:text="_tr('location.assignToHereConfirm', { name: hub.label, location: locationName })"
													@confirm="reassignHubToHere(hub.value)">
													<button
														type="button"
														class="btn btn-xs btn-soft btn-primary whitespace-nowrap"
														:disabled="isAssigningHub || !locationId"
														@click="close()">
														{{ _tr('location.assignToHere') }}
													</button>
												</ConfirmButton>
												<ConfirmButton
													v-if="hub.orphan"
													modal
													:title="_tr('location.unassign')"
													:text="_tr('location.unassignConfirm', { name: hub.label })"
													@confirm="deassignHub(hub.value)">
													<button
														type="button"
														class="btn btn-xs btn-soft btn-error whitespace-nowrap"
														:disabled="isAssigningHub"
														@click="close()">
														{{ _tr('location.unassign') }}
													</button>
												</ConfirmButton>
											</div>
										</div>
									</div>
								</div>
							</template>
						</Floater>
					</div>
				</div>

				<!-- COMAPP ASSIGN — same Floater pattern. -->
				<div class="border-base-300 bg-base-200/50 rounded-lg border p-4">
					<div class="flex items-center justify-between gap-2">
						<h3 class="text-base font-semibold">{{ _tr('location.assignComapp') }}</h3>
						<span class="text-xs text-base-content/60">{{ assignableComapps.length }}</span>
					</div>
					<div class="mt-3">
						<Floater
							placement="bottom-start"
							trigger-tag="div"
							trigger-class="block w-full"
							panel-class="w-[40rem] max-w-[92vw]"
							:close-on-outside="true"
							:auto-focus="false"
							@open="focusComappSearch"
							@close="comappSearch = ''">
							<button
								type="button"
								class="btn btn-block btn-outline justify-between gap-2"
								:disabled="!assignableComapps.length">
								<span>{{ _tr('location.selectComapp') }}</span>
								<ChevronDown :size="16" />
							</button>
							<template #content="{ close }">
								<div class="flex flex-col">
									<div class="sticky top-0 z-10 bg-base-100 border-b border-base-300/50 p-1">
										<input
											ref="comappSearchInput"
											v-model="comappSearch"
											type="search"
											class="input input-bordered w-full"
											:placeholder="_tr('location.searchPlaceholder')" />
									</div>
									<div class="max-h-96 overflow-auto">
										<p
											v-if="!filteredAssignableComapps.length"
											class="px-3 py-2 text-sm text-base-content/60">
											{{ assignableComapps.length ? _tr('location.noSearchMatch') : _tr('location.noAssignableComapps') }}
										</p>
										<div
											v-for="comapp in filteredAssignableComapps"
											:key="comapp.value"
											class="flex items-center gap-3 rounded px-2 py-1.5 hover:bg-base-200/60"
											:class="comapp.orphan ? 'border-l-2 border-warning/60' : comapp.unassigned ? 'border-l-2 border-info/60' : ''">
											<div class="min-w-0 flex-1">
												<div class="truncate font-medium">{{ comapp.label }}</div>
												<div
													class="text-xs"
													:class="comapp.orphan ? 'text-warning' : comapp.unassigned ? 'text-info' : 'text-base-content/60'">
													{{ comapp.locationLabel }}
												</div>
											</div>
											<div class="flex items-center gap-1 shrink-0">
												<ConfirmButton
													modal
													:title="_tr('location.assignToHere')"
													:text="_tr('location.assignToHereConfirm', { name: comapp.label, location: locationName })"
													@confirm="reassignComappToHere(comapp.value)">
													<button
														type="button"
														class="btn btn-xs btn-soft btn-primary whitespace-nowrap"
														:disabled="isAssigningComapp || !locationId"
														@click="close()">
														{{ _tr('location.assignToHere') }}
													</button>
												</ConfirmButton>
												<ConfirmButton
													v-if="comapp.orphan"
													modal
													:title="_tr('location.unassign')"
													:text="_tr('location.unassignConfirm', { name: comapp.label })"
													@confirm="deassignComapp(comapp.value)">
													<button
														type="button"
														class="btn btn-xs btn-soft btn-error whitespace-nowrap"
														:disabled="isAssigningComapp"
														@click="close()">
														{{ _tr('location.unassign') }}
													</button>
												</ConfirmButton>
										</div>
									</div>
								</div>
							</div>
							</template>
						</Floater>
					</div>
				</div>
			</div>

			<!-- Already-assigned panels — unchanged. -->
			<div class="grid gap-4 md:grid-cols-2">
				<div class="border-base-300 bg-base-200/50 rounded-lg border p-4">
					<div class="flex items-center justify-between gap-2">
						<h3 class="text-base font-semibold">{{ _tr('location.assignedHubs') }}</h3>
						<span class="text-xs text-base-content/60">{{ assignedHubs.length }}</span>
					</div>
					<p v-if="!assignedHubs.length" class="mt-3 text-sm text-base-content/60">
						{{ _tr('location.noAssignedHubs') }}
					</p>
					<div v-else class="mt-3 space-y-2 max-h-64 overflow-auto pr-1">
						<div
							v-for="hub in assignedHubs"
							:key="hub._id"
							class="border-base-300/60 bg-base-100/70 flex items-center justify-between gap-3 rounded-lg border px-3 py-2">
							<div class="min-w-0 flex items-center gap-2">
								<StatusLight type="hub" :_id="hub._id" />
								<div class="truncate font-medium">
									{{ hub.name || hub.serial || hub._id }}
								</div>
								<div v-if="hub.name && hub.serial" class="text-xs text-base-content/60">
									{{ hub.serial }}
								</div>
							</div>
							<ConfirmButton
								modal
								:title="_tr('location.unassign')"
								:text="_tr('location.unassignConfirm', { name: hub.name || hub.serial || hub._id })"
								@confirm="deassignHub(hub._id)">
								<button class="btn btn-sm btn-soft btn-error" :disabled="isAssigningHub">
									{{ _tr('location.unassign') }}
								</button>
							</ConfirmButton>
						</div>
					</div>
				</div>

				<div class="border-base-300 bg-base-200/50 rounded-lg border p-4">
					<div class="flex items-center justify-between gap-2">
						<h3 class="text-base font-semibold">{{ _tr('location.assignedComapps') }}</h3>
						<span class="text-xs text-base-content/60">{{ assignedComapps.length }}</span>
					</div>
					<p v-if="!assignedComapps.length" class="mt-3 text-sm text-base-content/60">
						{{ _tr('location.noAssignedComapps') }}
					</p>
					<div v-else class="mt-3 space-y-2 max-h-64 overflow-auto pr-1">
						<div
							v-for="comapp in assignedComapps"
							:key="comapp._id"
							class="border-base-300/60 bg-base-100/70 flex items-center justify-between gap-3 rounded-lg border px-3 py-2">
							<div class="min-w-0 flex items-center gap-2">
								<StatusLight type="comapp" :_id="comapp._id" />
								<div class="truncate font-medium">
									{{ comapp.pcName || comapp._id }}
								</div>
							</div>
							<ConfirmButton
								modal
								:title="_tr('location.unassign')"
								:text="_tr('location.unassignConfirm', { name: comapp.pcName || comapp._id })"
								@confirm="deassignComapp(comapp._id)">
								<button class="btn btn-sm btn-soft btn-error" :disabled="isAssigningComapp">
									{{ _tr('location.unassign') }}
								</button>
							</ConfirmButton>
						</div>
					</div>
				</div>

				<!-- Bottom close — re-fetches hubs+comapps so the parent
					 LocationView sidebar reflects any inline assignment changes. -->
				<div class="flex justify-end pt-2 border-t border-base-300/50">
					<button type="button" class="btn btn-primary" @click="closeModal">
						{{ _tr('location.applyAndClose') }}
					</button>
				</div>
			</div>
		</div>
	</VModalFinal>
</template>
